import { z, ZodObject, ZodRawShape } from 'zod';
import { queryStringSerializer, QueryStringUtils } from 'shared/queryStringSerializer';
import { compactObj, deepEqual, notNullOrEmpty } from 'shared/collection';
import { useCallback, useRef, useSyncExternalStore } from 'react';
import { Plain } from 'shared/type';
import { useLatest } from 'components/hooks';
const {
  windowQueryObject
} = QueryStringUtils;
let subscribers: (() => void)[] = [];
const notify = () => {
  for (const subscriber of subscribers) subscriber();
};
if (typeof window !== 'undefined') {
  window.addEventListener('popstate', notify);
  const originalPushState = history.pushState;
  history.pushState = function (...args) {
    originalPushState.apply(this, args);
    notify();
  };
  const originalReplaceState = history.replaceState;
  history.replaceState = function (...args) {
    originalReplaceState.apply(this, args);
    notify();
  };
}
const subscribe = (callback: () => void) => {
  subscribers.push(callback);
  return () => {
    subscribers = subscribers.filter(cb => cb !== callback);
  };
};
type QueryStringResult = Record<string, string | undefined>;

/**
 * A React hook to get and subscribe to values from the URL query string.
 * It only re-renders the component if the specified keys' values change.
 *
 * @param keys An array of query string keys to watch.
 * @returns An object with the current values of the specified keys.
 *
 * @example
 * const { tab, user } = useQueryString(['tab', 'user']);
 */
export const useQueryStringValues = <TPick extends Plain,>(pick: (r: QueryStringResult) => TPick, options?: {
  debugName?: string;
}): {
  values: TPick | undefined;
  setQuery: (q: Partial<QueryStringResult>) => void;
} => {
  const cachedResultRef = useRef<TPick>(undefined);
  const getSnapshot = useCallback(() => {
    const newValues = pick(windowQueryObject());
    const hasChanged = !deepEqual(newValues, cachedResultRef.current);
    if (options?.debugName) consoleDebug('useQueryStringValues.getSnapshot', options.debugName, {
      newValues,
      hasChanged,
      prevValues: cachedResultRef.current
    });
    if (!hasChanged) return cachedResultRef.current;
    cachedResultRef.current = newValues;
    return cachedResultRef.current;
  }, [pick]);
  const getServerSnapshot = useCallback(() => undefined, []);
  const values = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
  const setQuery = (q: Partial<QueryStringResult>) => {
    const existing = windowQueryObject();
    const next = compactObj({
      ...existing,
      ...q
    }) as QueryStringResult;
    if (deepEqual(existing, next)) return;
    const qs = QueryStringUtils.queryStringFromObject(next);
    const newUrl = `${window.location.pathname}${qs ? '?' + qs : ''}${window.location.hash ?? ''}`;
    history.replaceState(null, '', newUrl);
  };
  return {
    values,
    setQuery
  };
};
const {
  debug: consoleDebug
} = console;

/**
 * Subscribe to the query string given a zod schema. This will re-render only when the relevant parts of the query string change
 */
export const useQueryString = <T extends ZodRawShape, TPick extends Plain | unknown = unknown>(schema: ZodObject<T>, options?: {
  pick?: (r: z.infer<ZodObject<T>>) => TPick;
  debugName?: string;
}) => {
  const serializer = queryStringSerializer(schema);
  const {
    serializeToObject,
    serializeFromObject,
    deserializeFromObject
  } = serializer;
  type TParsed = z.infer<ZodObject<T>>;
  type TRes = unknown extends TPick ? z.infer<ZodObject<T>> : TPick;
  const latest = useLatest({
    pick: options?.pick,
    serializer
  });
  const qs = useQueryStringValues(useCallback((r: QueryStringResult): Plain => {
    if (!r) return undefined;
    const {
      pick,
      serializer: serializer_0
    } = latest.current;
    const parsedQuery = serializer_0.deserializeFromObject(r);
    return (pick ? pick(parsedQuery) : parsedQuery) as Plain;
  }, []), options);
  const query: TRes = qs.values ?? serializer.deserializeFromObject(windowQueryObject()) as any;
  const setQuery = (q: Partial<TParsed> | ((q: TParsed) => Partial<TParsed>), options_0?: {
    debugName?: string;
  }) => {
    if (typeof window == 'undefined') return;
    const existingQueryObject = windowQueryObject();
    const nextPartial = typeof q == 'function' ? q(serializer.deserializeFromObject(existingQueryObject)) : q;
    const next = compactObj({
      ...existingQueryObject,
      ...serializeToObject(nextPartial)
    }) as QueryStringResult;
    if (deepEqual(existingQueryObject, next)) return;
    const qs_0 = serializeFromObject(next);
    const newUrl = `${window.location.pathname}${qs_0 ? '?' + qs_0 : ''}${window.location.hash ?? ''}`;
    if (options_0?.debugName) consoleDebug('useQueryStringV2.setQuery', options_0.debugName, {
      existing: existingQueryObject,
      next,
      newUrl
    });
    history.replaceState(null, '', newUrl);
  };
  const queryStringRecord = (q_0: Partial<TParsed>) => compactObj({
    ...windowQueryObject(),
    ...serializeToObject(q_0)
  }) as QueryStringResult;
  const getQueryUrl = (q_1: Partial<TParsed>) => {
    const qs_1 = serializeFromObject(queryStringRecord(q_1));
    return `${window.location.pathname}${notNullOrEmpty(qs_1) ? '?' + qs_1 : ''}${window.location.hash ?? ''}`;
  };
  return {
    query,
    setQuery,
    getQueryUrl,
    queryStringRecord
  };
};
export type UseQueryString<T extends ZodRawShape, TPick extends Plain | unknown = unknown> = ReturnType<typeof useQueryString<T, TPick>>;